import React, { useContext, useEffect } from 'react';
import proptypes from 'prop-types';
const { func, string, bool, shape, arrayOf, oneOfType, number, instanceOf } = proptypes;
import { extend } from '@thd-nucleus/data-sources';
import { Drawer, DrawerHeader } from '@one-thd/sui-atomic-components';
import { AddToSelection } from './common/addto-selection/AddToSelection';
import { AddToProject } from './addto/project/AddToProject';
import { AddToList } from './addto/list/AddToList';
import { CopyProject } from './addto/copyProject/CopyProject';
import { AddToCart } from './addto/cart/AddToCart';
import { LoadingStatus } from './common/products-loading-status/LoadingStatus';
import { ProductExceptions } from './common/product-exceptions/ProductExceptions';
import { AddToContext } from './AddToContext';
import { ADD_TO_TYPE, SCREENS } from './AddToConstants';
import { handleOpenATQ } from './AddToHelpers';
import { DisplayItemDetails } from './addto/displayItemDetails/DisplayItemDetails';

export const AddToDrawer = ({
  open = false,
  setOpen,
  setAddToType,
  updateLoadedProductsData,
  showBackButton,
  loadedProductsData,
  projectDetails,
  loadingProgress,
  list,
  initialLoad,
  setInitialLoad,
  isProjectsEligible,
  copyProjectData,
  nonQuoteableProducts,
  selectedCatalogItems,
  selectedQuotableCatalogItemDetails,
  productPodItemData,
  addToQuoteRef,
  aiListBuilderData,
  isDisplayOnly
}) => {

  const {
    screen, addToType, handleClose, setAbandonWarning, canShowWarning, products, atcDrawerType
  } = useContext(AddToContext);

  const onDrawerClose = (__event) => {
    if (canShowWarning) {
      setAbandonWarning({ show: true, action: 'CLOSE' });
      return;
    }
    handleClose(__event);
  };

  useEffect(() => {
    window.LIFE_CYCLE_EVENT_BUS.on('cart.add-to-cart', (response) => {
      setOpen(false);
    });
  }, []);

  const { ADD_TO_SELECTION, LOADING_STATUS, PRODUCT_EXCEPTIONS } = SCREENS;

  const drawerProps = { style: { maxWidth: '500px', gridColumnStart: '-1' } };

  return (
    <Drawer
      open={open}
      onClose={onDrawerClose}
      DrawerRootProps={addToType === 'Cart' ? drawerProps : null}
    >
      <>
        {screen === ADD_TO_SELECTION && (
          <AddToSelection
            products={products}
            setAddToType={setAddToType}
            handleClose={handleClose}
            isProjectsEligible={isProjectsEligible}
            copyProjectData={copyProjectData?.items}
          />
        )}
        {
          (addToType === 'Project' || atcDrawerType === 'Project') && (
            <AddToProject
              products={products}
              projectId={projectDetails?.id}
              initialLoad={initialLoad}
              setInitialLoad={setInitialLoad}
              showBackButton={showBackButton}
              productPodItemData={productPodItemData}
              aiListBuilderData={aiListBuilderData}
              isDisplayOnly={isDisplayOnly}
            />
          )
        }
        {
          (addToType === 'List' || atcDrawerType === 'List') && (
            <AddToList
              products={selectedCatalogItems ?? products}
              showCount={list?.showCount}
              skipListId={list?.skipListId}
              isSharedList={list?.isSharedList}
              listId={list?.listId}
              onMaterialsAdded={list?.onMaterialsAdded}
              aiListBuilderData={aiListBuilderData}
              productPodItemData={productPodItemData}
            />
          )
        }
        {
          (addToType === 'CopyProject') && (
            <CopyProject
              projectId={projectDetails?.id}
              copyProjectData={copyProjectData}
              initialLoad={initialLoad}
              setInitialLoad={setInitialLoad}
              showBackButton={showBackButton}
            />
          )
        }
        {
          (addToType === ADD_TO_TYPE.QUOTE) && (
            <>
              <DrawerHeader
                title={`Add to ${addToType}`}
                onClose={handleClose}
              />
              {screen === LOADING_STATUS && (
                <LoadingStatus
                  products={products}
                  loadingProgress={loadingProgress}
                  projectDetails={projectDetails}
                  loadedProductsData={loadedProductsData}
                  updateLoadedProductsData={updateLoadedProductsData}
                />
              )}
              {screen === PRODUCT_EXCEPTIONS && (
                <ProductExceptions
                  productExceptions={nonQuoteableProducts}
                  handleOpenATQ={() => handleOpenATQ(addToQuoteRef)}
                  showATQ={selectedQuotableCatalogItemDetails?.length > 0}
                />
              )}
            </>
          )
        }
        {
          (addToType === ADD_TO_TYPE.CART && !atcDrawerType) && (
            <AddToCart
              products={products}
              productPodItemData={productPodItemData}
            />
          )
        }
        {
          (addToType === 'DisplayItemDetails') && (
            <DisplayItemDetails
              products={products}
              showBackButton
            />
          )
        }
      </>
    </Drawer>
  );
};

const safeHTMLButtonPropType = typeof HTMLButtonElement !== 'undefined'
  ? instanceOf(HTMLButtonElement)
  : () => null;

AddToDrawer.propTypes = {
  list: shape({
    showCount: bool,
    isSharedList: bool,
    showIconButton: bool,
    skipListId: string,
    listId: string,
    onMaterialsAdded: func,
    isMaterialsButton: bool
  }),
  aiListBuilderData: shape({
    onNewIntentsSave: func,
    title: string,
    refType: string,
    refId: string,
    materials: arrayOf(shape({
      groupName: string,
      products: arrayOf(shape({
        quantity: number,
        product: string,
      }))
    }))
  }),
  setAddToType: func.isRequired,
  showBackButton: bool.isRequired,
  open: bool.isRequired,
  setOpen: func.isRequired,
  isProjectsEligible: bool.isRequired,
  setInitialLoad: func.isRequired,
  initialLoad: bool.isRequired,
  loadingProgress: number,
  loadedProductsData: arrayOf(shape({})),
  nonQuoteableProducts: arrayOf(shape({})),
  selectedQuotableCatalogItemDetails: arrayOf(shape({})),
  selectedCatalogItems: arrayOf(shape({})),
  projectDetails: shape({
    id: string,
    projectId: string,
    projectName: string,
    storeId: string,
    zipCode: string,
    preferredStore: string,
    assortmentZip: string,
    addresses: arrayOf(shape({
      addressType: string,
      id: string
    }))
  }),
  copyProjectData: shape({
    sourceList: string,
    items: arrayOf(
      oneOfType([string, shape({})])
    ),
    targetProjectId: string
  }),
  updateLoadedProductsData: func.isRequired,
  productPodItemData: shape({
    itemId: string,
    quantity: number,
    searchKeyword: string,
    title: string,
    materials: arrayOf(shape({
      id: string,
      name: string
    }))
  }),
  addToQuoteRef: oneOfType([
    func,
    shape({ current: safeHTMLButtonPropType })
  ]),
  isDisplayOnly: bool
};

AddToDrawer.defaultProps = {
  list: null,
  loadingProgress: 0,
  selectedQuotableCatalogItemDetails: [],
  loadedProductsData: [],
  projectDetails: {},
  copyProjectData: {},
  nonQuoteableProducts: [],
  selectedCatalogItems: null,
  productPodItemData: {},
  addToQuoteRef: null,
  aiListBuilderData: {},
  isDisplayOnly: false
};

AddToDrawer.displayName = 'AddToDrawer';

AddToDrawer.dataModel = extend(
  AddToProject,
  AddToList,
  LoadingStatus,
  CopyProject
);
